import dayjs from 'dayjs'
import { HistoryLesson, ScheduleHistoryPresentation } from '@/api/changeHistory'
import cn from './index.module.css'
import { normalizeTiptapValue } from '@/helpers/new/normalizeTiptapValue'
import { getMessageText } from '@/helpers/messageText'
import { Tooltip } from 'antd'

const changeText = (value: string) => {
  const document = normalizeTiptapValue(value)
  return document?.type === 'doc' ? getMessageText(document) || 'Пустой комментарий' : value || '—'
}

const lessonDate = (lesson?: HistoryLesson) => {
  if (!lesson) return '—'
  const date = lesson.date && dayjs(lesson.date).isValid() ? dayjs(lesson.date).format('DD.MM.YYYY') : ''
  return [date, [lesson.start_time, lesson.end_time].filter(Boolean).join('–')].filter(Boolean).join(' ') || '—'
}

const StudentNames = ({ lesson }: { lesson: HistoryLesson }) => {
  const names = lesson.student_names?.length ? lesson.student_names : lesson.students.split(', ').filter(Boolean)
  return names.length > 3 ? <>
    {names.slice(0, 3).join(', ')}{' '}
    <Tooltip title={<div style={{ maxHeight: 300, overflowY: 'auto' }}>{names.map((name, index) => <div key={index}>{name}</div>)}</div>}>
      <span tabIndex={0} aria-label="Полный список учащихся">… (+{names.length - 3})</span>
    </Tooltip>
  </> : <>{names.join(', ')}</>
}

export const ScheduleLessonCell = ({ value }: { value: ScheduleHistoryPresentation }) => (
  <div className={cn.cell}>
    {(value.action !== 'delete' && value.after.length ? value.after : value.before).map((lesson, index) => (
      <div key={`${lesson.id}-${index}`} className={cn.lesson}>
        <div className={cn.lessonTitle}>{lesson.subject || 'Занятие'}</div>
        <div className={cn.meta}>{[lesson.lesson_type, lesson.recurrence].filter(Boolean).join(' · ')}</div>
        <div className={cn.lessonDate}>
          {value.action === 'create' && lesson.recurrence && <span className={cn.meta}>Дата начала: </span>}
          {lessonDate(lesson)}
        </div>
        <div><span className={cn.meta}>Педагог: </span>{lesson.teacher || 'Не назначен'}</div>
        {lesson.groups && <div><span className={cn.meta}>Группа: </span>{lesson.groups}</div>}
        {lesson.students && (!lesson.groups || lesson.lesson_type === 'Вводное') && <div><span className={cn.meta}>Учащиеся: </span><StudentNames lesson={lesson} /></div>}
      </div>
    ))}
  </div>
)

const ValueChange = ({ label, before, after }: { label: string; before: string; after: string }) => (
  <div className={cn.change}>
    <div className={cn.changeLabel}>{label}</div>
    {label.startsWith('Форматирование комментария') && changeText(before) === changeText(after) && (
      <div className={cn.meta}>Изменено оформление, текст сохранён.</div>
    )}
    <div className={cn.valueChange}>
      <div className={cn.oldValue}><span className={cn.valueLabel}>Было</span>{changeText(before)}</div>
      <span className={cn.arrow} aria-label="изменено на">→</span>
      <div className={cn.newValue}><span className={cn.valueLabel}>Стало</span>{changeText(after)}</div>
    </div>
  </div>
)

export const ScheduleChangesCell = ({ value }: { value: ScheduleHistoryPresentation }) => (
  <div className={cn.cell}>
    <div className={cn.scope}>{value.scope}</div>
    {value.date_range && <div>Удалены занятия с {dayjs(value.date_range.from).format('DD.MM.YYYY')} по {dayjs(value.date_range.to).format('DD.MM.YYYY')}</div>}
    {value.action === 'swap' && value.before.map((lesson, index) => (
      <ValueChange key={`${lesson.id}-${index}`} label={lesson.subject || `Занятие ${index + 1}`} before={lessonDate(lesson)} after={lessonDate(value.after[index])} />
    ))}
    {value.action === 'copy' && <ValueChange label="Скопировано на другую дату" before={lessonDate(value.before[0])} after={lessonDate(value.after[0])} />}
    {value.action === 'create' && value.before[0] && <div className={cn.meta}>
      К занятию: {value.before[0].subject} · {lessonDate(value.before[0])}
      {value.before[0].groups ? ` · группа ${value.before[0].groups}` : ''}
    </div>}
    {value.changes.filter(change => value.action !== 'copy' || change.label !== 'Дата и время').map(change => (
      <ValueChange key={change.label} label={change.label} before={change.old} after={change.new} />
    ))}
  </div>
)
